import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Select } from 'antd';
import moment from 'moment';
import { normalizedInitialResolvingArea } from '~/common/tickets';
import { getLoggedUser } from '~/store/user/actions';

import { Flex, SearchBar, SelectDate, Typography, Tooltip } from '~/ui/components';
import { Calendar, LoadingSpinner } from '~/ui/assets/icons';
import { useDrawer } from '~/hooks/useDrawer';
import { useAwaitControl } from 'react-redux-await-control';
import {
  getTicketDistributionConfig,
  getTicketResolvingAreas,
  updateTicketDistributionFlag,
} from '~/store/tickets/actions';
import { getDistributionFlagOptions } from '~/store/settings/actions';
import { StyledSelect } from '~/components/Ticket/components/Actions/Actions.styles';
import { AllFilters, SLA_OPTIONS } from '~/components';
import { StatusSelect } from '../StatusSelect/StatusSelect';
import { ResponsibleSelect } from '../ResponsibleSelect/ResponsibleSelect';
import {
  Box,
  Content,
  MoreFilters,
  SearchWrapper,
  FilterWrapper,
  Wrapper,
  UpdateButton,
  DownloadButton,
  FlagDot,
  FlagOptionLabel,
  FlagSelect,
  FlagSelectGlobalStyles,
} from './InlineFilters.styles';

export type FilterValues = {
  search?: string;
  date?: [string, string];
  statusId?: number;
  statusIds?: number[];
  resolvingareaId?: number;
  groupResolvingAreaId?: number;
  responsibleId?: number;
  sla?: string;
  justificationIds?: number[] | string[];
  sort?: string;
  bankName?: string;
  financialInstitutionName?: string;
  product?: string;
  priorityTicket?: string;
  categoryId?: number;
  reasonId?: number;
  journeyId?: number;
  channelId?: number;
  withoutCategory?: boolean;
  withoutResolvingArea?: boolean;
  withoutJourney?: boolean;
  withoutSla?: boolean;
  withoutProduct?: boolean;
};

type DistributionFlagOption = {
  id: number;
  name: string;
  enabledOption: boolean;
  active: boolean;
  displayOrder: number;
};

type FiltersProps = {
  onChange?: (value: any) => void;
  onClear?: () => void;
  filters?: FilterValues;
  title: string;
  availableFilters?: string[];
  hiddenFilters?: string[];
  defaultFilter?: string;
  distributionFlag?: boolean;
  onDistributionChange?: (value: boolean) => void;
  disabled?: boolean;
  onDownload?: () => void;
  loadingTickets?: boolean;
};

export function InlineFilters({
  onChange,
  filters,
  onClear,
  title,
  availableFilters,
  hiddenFilters,
  defaultFilter,
  distributionFlag,
  onDistributionChange,
  disabled,
  onDownload,
  loadingTickets,
}: FiltersProps) {
  const { openDrawer } = useDrawer();
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isCoolingDown, setIsCoolingDown] = useState(false);

  const getTicketResolvingAreasControl = useAwaitControl(getTicketResolvingAreas);
  const isLoadingResolvingAreas = getTicketResolvingAreasControl.isRunning();
  const ticketResolvingAreas = getTicketResolvingAreasControl.result();

  const updateDistributionFlagControl = useAwaitControl(updateTicketDistributionFlag);
  const updateDistributionFlagLoading = updateDistributionFlagControl.isRunning();
  const updateDistributionFlag = updateDistributionFlagControl.result();

  const distributionConfigControl = useAwaitControl(getTicketDistributionConfig);
  const distributionConfigLoading = distributionConfigControl.isRunning();
  const distributionConfig = distributionConfigControl.result();

  const flagOptionsControl = useAwaitControl(getDistributionFlagOptions);
  const flagOptions: DistributionFlagOption[] = flagOptionsControl.result() || [];
  const isFlagOptionsLoading = flagOptionsControl.isRunning();

  const getLoggedUserControl = useAwaitControl(getLoggedUser);
  const loggedUser = getLoggedUserControl.result();

  const isLoading = useMemo(
    () => distributionConfigLoading || updateDistributionFlagLoading || !loggedUser?.id,
    [distributionConfigLoading, updateDistributionFlagLoading, loggedUser],
  );

  const filterIsVisible = useCallback(
    (filterName: string) => {
      if (hiddenFilters?.includes(filterName)) return false;
      if (!availableFilters?.length) return true;

      return availableFilters.includes(filterName);
    },
    [availableFilters],
  );

  const statusSelectValue = useMemo(() => {
    const raw = filters?.statusIds || filters?.statusId;
    if (raw == null) return undefined;
    return Array.isArray(raw) ? raw : [raw];
  }, [filters?.statusIds, filters?.statusId]);

  const downloadDateRangeValid = useMemo(() => {
    const start = filters?.date?.[0];
    const end = filters?.date?.[1];
    if (!start || !end) return false;
    return moment(end).diff(moment(start), 'days') + 1 <= 30;
  }, [filters?.date]);

  const downloadDisabledReason = useMemo(() => {
    if (loadingTickets) return 'Aguarde a listagem carregar para baixar';
    if (!downloadDateRangeValid) return 'Selecione um período de até 30 dias no filtro de data para baixar';
    return null;
  }, [loadingTickets, downloadDateRangeValid]);

  const handleChangeWithDebounce = debounce((search) => {
    onChange?.({ search });
  }, 500);

  const handleMoreFilters = useCallback(() => {
    openDrawer(
      'more-filters-drawer',
      <AllFilters onChange={onChange} initialValues={filters} onClear={onClear} hiddenFilters={hiddenFilters} />,
      {
        title: 'Mais filtros',
        width: 557,
        closable: true,
        maskClosable: true,
        bodyStyle: { overflow: 'visible' },
      },
    );
  }, [filters]);

  const handleOnChange = useCallback(
    (selector: string, value, lastOfArray = false) => {
      function validateArray(value: any[]) {
        if (lastOfArray) return value.at(-1);
        if (value?.length === 1) return value?.[0];
        return value;
      }
      onChange?.({ [selector]: Array.isArray(value) ? validateArray(value) : value });
    },
    [onChange],
  );

  // Internal: called programmatically. No cooldown.
  const applyDistributionFlagChange = useCallback(
    (optionId: number) => {
      if (!loggedUser?.id) return;
      const option = flagOptions.find((o) => o.id === optionId);
      onDistributionChange?.(option?.enabledOption ?? false);
      updateDistributionFlagControl.start({
        id: loggedUser?.id,
        enabled: option?.enabledOption ?? false,
        optionId,
        loggedUser: loggedUser?.name?.trim() || `Agente ${loggedUser.id}`,
      });
    },
    [loggedUser, flagOptions], // updateDistributionFlagControl and onDistributionChange intentionally omitted
  );

  // User-facing: called from Select onChange. Activates 300ms cooldown before delegating.
  const handleDistributionFlagChange = useCallback(
    (optionId: number) => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      setIsCoolingDown(true);
      cooldownTimerRef.current = setTimeout(() => setIsCoolingDown(false), 300);
      setSelectedOptionId(optionId);
      applyDistributionFlagChange(optionId);
    },
    [applyDistributionFlagChange],
  );

  useEffect(() => {
    if (!ticketResolvingAreas.length) {
      getTicketResolvingAreasControl.start();
    }
    flagOptionsControl.start();
  }, []);

  useEffect(() => {
    if (loggedUser?.id) distributionConfigControl.start({ id: loggedUser?.id });
  }, [loggedUser]);

  useEffect(() => {
    if (distributionConfig) {
      const optionId = distributionConfig.distributionFlagOptionId ?? null;
      setSelectedOptionId(optionId);
      onDistributionChange?.(distributionConfig.distributionflag);
    }
  }, [distributionConfig]);

  useEffect(() => {
    if (updateDistributionFlag) {
      const optionId = updateDistributionFlag.distributionFlagOptionId ?? null;
      setSelectedOptionId(optionId);
    }
  }, [updateDistributionFlag]);

  useEffect(
    () => () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    },
    [],
  );

  return (
    <Wrapper>
      <FlagSelectGlobalStyles />
      <Flex ph={16} pv={24} justify="space-between">
        <Flex>
          <Typography type="headingH6" weight={600}>
            {title}
          </Typography>
        </Flex>
        <Flex align="center" justify="flex-end" gap={32} flex={0}>
          {distributionFlag && (
            <Flex gap={8} align="center" justify="flex-end">
              {isLoading && <LoadingSpinner />}

              {!distributionConfig?.resolvingAreaId ? (
                <Tooltip
                  small
                  content="Funcionalidade indisponível. Procure a liderança para solicitar seu vínculo à uma área resolvedora."
                >
                  <div>
                    <FlagSelect
                      placeholder="Status de distribuição"
                      dropdownClassName="flag-select-dropdown"
                      disabled
                    />
                  </div>
                </Tooltip>
              ) : (
                <FlagSelect
                  placeholder="Status de distribuição"
                  disabled={isLoading || isCoolingDown}
                  value={selectedOptionId ?? undefined}
                  onChange={(val: number) => handleDistributionFlagChange(val)}
                  loading={isFlagOptionsLoading}
                  dropdownClassName="flag-select-dropdown"
                >
                  {flagOptions.map((opt) => (
                    <Select.Option key={opt.id} value={opt.id}>
                      <FlagOptionLabel>
                        <FlagDot enabled={opt.enabledOption} />
                        {opt.name}
                      </FlagOptionLabel>
                    </Select.Option>
                  ))}
                </FlagSelect>
              )}
            </Flex>
          )}

          {onDownload &&
            (downloadDisabledReason ? (
              <Tooltip content={downloadDisabledReason}>
                <div>
                  <DownloadButton disabled iconPosition="left">
                    Baixar dados
                  </DownloadButton>
                </div>
              </Tooltip>
            ) : (
              <DownloadButton onClick={onDownload} iconPosition="left">
                Baixar dados
              </DownloadButton>
            ))}

          <UpdateButton disabled={isLoading || disabled} onClick={() => onChange?.({})} iconPosition="left">
            Atualizar
          </UpdateButton>
        </Flex>
      </Flex>

      <FilterWrapper defaultFilter={defaultFilter} disabled={disabled}>
        <SearchWrapper>
          <SearchBar
            placeholder="Buscar pelo ticket"
            onChange={handleChangeWithDebounce}
            initialValue={filters?.search}
          />
        </SearchWrapper>

        <Content>
          {filterIsVisible('sla') && (
            <Box className="filter-selector-sla">
              <StyledSelect
                placeholder="SLA"
                width={150}
                value={filters?.sla ? [filters?.sla] : null}
                options={SLA_OPTIONS}
                onChange={(option) => handleOnChange('sla', option, true)}
              />
            </Box>
          )}

          {filterIsVisible('date') && (
            <Box className="filter-selector-date" active={!!(filters?.date?.[0] && filters?.date?.[1])}>
              <SelectDate
                icon={<Calendar />}
                dateFormat="DD/MM/YY"
                selectRange
                value={filters?.date ? filters.date : null}
                maxDate={new Date()}
                placeholder="Filtrar por data"
                onChange={(value) => handleOnChange('date', value)}
              />
            </Box>
          )}

          {filterIsVisible('statusId') && (
            <Box className="filter-selector-statusId">
              <StatusSelect value={statusSelectValue} onChange={(value) => onChange?.({ statusIds: value })} multiple />
            </Box>
          )}

          {filterIsVisible('resolvingareaId') && (
            <Box className="filter-selector-resolvingareaId">
              <StyledSelect
                placeholder="Área resolvedora"
                value={
                  normalizedInitialResolvingArea(
                    ticketResolvingAreas,
                    filters?.groupResolvingAreaId ?? filters?.resolvingareaId,
                  ) ?? []
                }
                options={ticketResolvingAreas}
                loading={isLoadingResolvingAreas}
                changeOnSelect
                onChange={(value: (string | number)[], selectedOptions: any[]) => {
                  const last = selectedOptions?.at(-1);
                  const isGroup = (last?.children?.length ?? 0) > 0;
                  const id = value?.at(-1) as number | undefined;
                  if (isGroup) {
                    onChange?.({ groupResolvingAreaId: id ?? null, resolvingareaId: null });
                  } else {
                    onChange?.({ resolvingareaId: id ?? null, groupResolvingAreaId: null });
                  }
                }}
                fieldNames={{ label: 'name', value: 'id' }}
              />
            </Box>
          )}

          {filterIsVisible('responsibleId') && (
            <ResponsibleSelect
              className="filter-selector-responsibleId"
              value={filters?.responsibleId}
              onChange={(value) => handleOnChange('responsibleId', value)}
              width={180}
            />
          )}

          <MoreFilters icon="Filters" variant="outline" size="lg" onClick={handleMoreFilters}>
            Mais filtros
          </MoreFilters>
        </Content>
      </FilterWrapper>
    </Wrapper>
  );
}
